import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import Layer from 'ol/layer/Layer';
import TileLayer from 'ol/layer/Tile';
import Projection from 'ol/proj/Projection';
import TileWMS from 'ol/source/TileWMS';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { StedOplysninger } from 'src/app/api/models';
import { CalculatedKonflikt } from 'src/app/projekter/pages/forhold-paa-stedet/konfliktvisning/models/calculated-konflikt';
import { StedMapComponent } from '../sted-map/sted-map.component';

@Component({
  selector: 'bom-konflikt-map',
  templateUrl: './konflikt-map.component.html',
  styleUrls: ['./konflikt-map.component.scss'],
})
export class KonfliktMapComponent implements OnInit, OnDestroy {
  @Input()
  stedOplysninger$: Observable<StedOplysninger>;

  @Input()
  calculatedKonflikter$: Observable<CalculatedKonflikt[]>;

  @Input()
  enableMeasurement = false;

  @ViewChild(StedMapComponent, { static: true })
  stedmap: StedMapComponent;

  konfliktLayers$ = new BehaviorSubject<Layer[]>([]);
  konfliktTyperWithConflicts$: Observable<CalculatedKonflikt[]>;
  otherKonfliktTyper$: Observable<CalculatedKonflikt[]>;
  showOtherLayers = false;
  checkedLayers: { [key: string]: boolean } = {};

  private componentDestroyed$ = new Subject();

  constructor() {}

  ngOnInit() {
    this.konfliktTyperWithConflicts$ = this.calculatedKonflikter$.pipe(
      map(konflikter => konflikter.filter(konflikt => konflikt.status === 'Conflict')),
      map(konfliktMapLayer =>
        Array.from(
          new Set(
            konfliktMapLayer
              .map(k => k.kort.kode)
              .map(kode => konfliktMapLayer.find(k => k.kort.kode === kode))
          )
        )
      ),
      tap(konflikter => {
        this.checkedLayers = {};
        for (let i = 0; i < konflikter.length; i++) {
          const konflikt = konflikter[i];
          this.setLayerVisible(i === 0, konflikt.kort.kode);
        }
      }),
      shareReplay()
    );

    this.otherKonfliktTyper$ = this.calculatedKonflikter$.pipe(
      map(konfliktMapLayer =>
        Array.from(
          new Set(
            konfliktMapLayer
              .map(k => k.kort.kode)
              .map(kode => konfliktMapLayer.find(k => k.kort.kode === kode))
          )
        )
      ),
      map(konflikter =>
        konflikter.filter(
          konflikt => konflikt.status !== 'Conflict' && konflikt.status !== 'Inconclusive'
        )
      ),
      shareReplay()
    );

    this.calculatedKonflikter$
      .pipe(
        map(konflikter => this.layersFromCalculatedKonflikter(konflikter)),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(layers => {
        this.showOtherLayers = false;
        this.konfliktLayers$.next(layers);
      });
  }

  layersFromCalculatedKonflikter(calculatedKonflikter: CalculatedKonflikt[]): Layer[] {
    const layers = new Array<Layer>();
    for (const konfliktType of calculatedKonflikter) {
      const kort = konfliktType.kort;
      const sourceTileWMS = new TileWMS({
        url: kort.wmsUrl,
        params: {
          LAYERS: kort.wmsTema,
          TRANSPARENT: 'TRUE',
        },
        projection: new Projection({
          code: 'EPSG:25832',
          extent: [120000, 5661139.2, 1000000, 6500000],
          units: 'm',
        }),
      });

      const layer = new TileLayer({
        extent: [120000, 5661139.2, 1000000, 6500000],
        opacity: 1.0,
        visible: false,
        source: sourceTileWMS,
        maxResolution: kort.maxZoom ? kort.maxZoom : Infinity,
        minResolution: kort.minZoom ? kort.minZoom : 0,
      });
      layer.set('id', kort.kode);
      layers.push(layer);
    }
    return layers;
  }

  setLayerVisible(isChecked: boolean, id: string) {
    this.checkedLayers[id] = isChecked;
    this.stedmap.setLayerVisible(isChecked, id);
    this.stedmap.enableMatrikelSelect = false;
  }

  toggleOtherLayers() {
    this.showOtherLayers = !this.showOtherLayers;
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }
}
